import { scaleSqrt } from 'd3';
import { extent } from 'd3-array';
import { useMemo } from 'react';

import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
} from '@revelio/data-access';

import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getFormattedDateString } from '../../../filters/utils/dateUtils';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import { useChart } from '../../Chart/ChartProvider';
import type { MapPoint } from '../../Chart/Map/types';
import { MAX_RADIUS, MIN_RADIUS } from '../../Chart/Map/utils/constants';
import { determineLocationType } from '../../Chart/Map/utils/location-type-utils';
import { normalizePointValue } from '../../Chart/Map/utils/normalization';
import { isCompositionRoutableMetric } from '../../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataProviderQueryStatus } from '../types';
import {
  extractGranularitiesWithIds,
  useCoordinateResolverForMixedGranularities,
} from '../utils/coordinate-resolver';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';

export type CompositionMapData = {
  points: MapPoint[];
};

type CompositionMapDataProviderProps = {
  primaryFilterId: PrimaryFilterId;
  children: DataProviderChildren<CompositionMapData | undefined>;
};

export const CompositionMapDataProvider = ({
  primaryFilterId: primaryDimension,
  children,
}: CompositionMapDataProviderProps) => {
  const { item } = useChart();
  const normalization = item?.plot?.mapOptions?.normalization;
  const transformation = item?.plot?.mapOptions?.transformation;
  const metric = usePlotMetric();

  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const { activeFilterMap, geographyMode } = useFilterContext();

  const baseVarsV3 = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    getSelectionBreakdowns: true,
    isSnapshot: true,
    geographyMode,
    activeFilterMap,
  });

  const endDate =
    baseVarsV3.filters.end_date ??
    getFormattedDateString(availableDateRange?.month.max, 'month');
  const startDate =
    baseVarsV3.filters.start_date ??
    getFormattedDateString(availableDateRange?.month.min, 'month');

  const variablesV3: CompositionV3DataQueryVariables = {
    ...baseVarsV3,
    filters: {
      ...baseVarsV3.filters,
      start_date: startDate,
      end_date: endDate,
    },
  };

  const [{ data: dataV3, status: statusV3 }] =
    useUrqlQueryWithStatus<CompositionV3DataQuery>({
      query: COMPOSITION_GET_DATA_V3,
      variables: variablesV3,
      pause: false,
    });

  const geoTotals = (() => {
    const totals = new Map<number, { value: number; label: string }>();
    const entities = dataV3?.composition_v3?.entities ?? [];
    entities.forEach((ent) => {
      const geoBreakdown = ent.metrics?.geography ?? [];
      geoBreakdown.forEach((g) => {
        const hc = g.headcount;
        const last = hc[hc.length - 1];
        const value = last ? last.count : 0;
        const id = g.id;
        const name = g.short_name ?? '';
        if (!id || value <= 0) return;
        const current = totals.get(id) ?? { value: 0, label: name };
        current.value += value;
        current.label = name || current.label;
        totals.set(id, current);
      });
    });
    return Array.from(totals.entries()).map(([id, { value, label }]) => ({
      id,
      value,
      label,
    }));
  })();

  const granularitiesWithIds = extractGranularitiesWithIds(geoTotals);

  const { data: coords, loading: coordsLoading } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const points: MapPoint[] = useMemo(() => {
    if (!coords.length || !geoTotals.length) return [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    const filtered = geoTotals.filter((g) => coordMap.has(g.id));
    const valueExtent = extent(
      filtered
        .map((d) => {
          const c = coordMap.get(d.id);
          if (!c) return null;

          const locationType = determineLocationType(
            d.label || c.name,
            c.granularity
          );

          const { value: v } = normalizePointValue(
            d.value,
            metric,
            locationType,
            String(d.id),
            { normalization, transformation }
          );
          return v;
        })
        .filter((v): v is number => v !== null)
    );

    if (
      !valueExtent[0] ||
      !valueExtent[1] ||
      valueExtent[0] === valueExtent[1]
    ) {
      return [];
    }

    const scale = scaleSqrt()
      .domain(valueExtent as [number, number])
      .range([MIN_RADIUS, MAX_RADIUS]);

    return filtered
      .map((d) => {
        const c = coordMap.get(d.id);
        if (!c) return null;

        const label = d.label || c.name;

        const locationType = determineLocationType(label, c.granularity);

        const { value } = normalizePointValue(
          d.value,
          metric,
          locationType,
          String(d.id),
          { normalization, transformation }
        );

        return {
          id: String(d.id),
          label,
          lat: c.lat,
          lon: c.lon,
          value,
          circleRadius: scale(value),
          locationType,
        };
      })
      .filter((point): point is NonNullable<typeof point> => point !== null);
  }, [coords, geoTotals, metric, normalization, transformation]);

  if (!isCompositionRoutableMetric(metric)) {
    return children({
      data: { points: [] },
      status: DataProviderQueryStatus.Success,
    });
  }

  const finalStatus = coordsLoading
    ? DataProviderQueryStatus.Fetching
    : statusV3;

  return children({
    data: { points },
    status: finalStatus,
  });
};
